import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Heart, KeyRound } from "lucide-react";
import PasswordInput from "@/components/app/PasswordInput";
import { useT } from "@/context/LanguageContext";
import { api, detailText } from "@/lib/api";
import { toast } from "sonner";

export default function FirstLogin() {
  const { t } = useT();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error(t("auth.password_too_short"));
    if (password !== confirm) return toast.error(t("auth.first_login_mismatch"));
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: "", new_password: password });
      toast.success(t("auth.first_login_success"));
      navigate("/login");
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || t("settings.invite_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-6 bg-background">
      <form onSubmit={submit} className="w-full max-w-md" data-testid="first-login-page">
        <div className="mb-8 flex items-center gap-2">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Heart className="h-5 w-5" />
          </div>
          <span className="font-serif text-2xl">Weddly</span>
        </div>
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-5">
          <KeyRound className="h-6 w-6" />
        </div>
        <h1 className="font-serif text-3xl mb-2">{t("auth.first_login_title")}</h1>
        <p className="text-muted-foreground mb-8 leading-relaxed">{t("auth.first_login_desc")}</p>

        <div className="space-y-3 mb-6">
          <div>
            <Label>{t("auth.new_password")}</Label>
            <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} data-testid="first-login-new-password" />
          </div>
          <div>
            <Label>{t("auth.confirm_new_password")}</Label>
            <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="first-login-confirm-password" />
          </div>
        </div>

        <Button type="submit" disabled={loading} className="w-full h-12 rounded-full text-base font-medium" data-testid="first-login-submit">
          {loading ? "..." : t("settings.create_account")}
        </Button>
      </form>
    </div>
  );
}
